// Utility functions for constructing chart configs.
// On the one hand we have JSON reports.
// On the other hand we have charts based on those reports.
// In the middle we need a thin layer of mediation that
// looks at the reports, then tells charts what to render.
// All the functions in this util are exported for the
// purposes of testing, but only a tiny handful are
// intended for public consumptions. The only ones
// you should use are the ones that return complete
// configurations for different chart types:
// getGroupsForComparisons
// getGroupsForCompetitionRankings
// getGroupsForHeatmap
// getGroupsForPointTrends
// getGroupsForStackedBreakdown
// getGroupsForTrends
// getGroupsForYearOverYearTrends
// Figure out which kind of chart you need, pass the
// report to the corresponding function, and you should
// get back what you need.
// If you're not familiar with how the charts work, see
// modules/javascript/www/charts/README.md

import chroma from 'chroma-js';
import {COLUMN_LAYOUT, FULL_WIDTH_LAYOUT} from 'charts/constants/layout-mode';
import {
  BAR_SERIES,
  BAR_STEP_SERIES,
  CLUSTERED_COLUMN_SERIES,
  COLUMN_SERIES,
  COLUMN_STEP_SERIES,
  DIFFERENCE_BAR_SERIES,
  DIFFERENCE_COLUMN_SERIES,
  DOT_SERIES,
  LINE_SERIES,
  STACKED_COLUMN_SERIES,
} from 'charts/constants/series-type';
import {STRIPE} from 'charts/constants/mask';
import {getTimeTickFormat} from 'charts/utils/tick-util';
import {formatDate} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import {
  DateTimeFormat,
  ReportChartTypeEnum,
  ReportComparisonEnum,
  ReportDifferenceColorEnum,
  ReportOrderEnum,
  ReportingPeriodEnum,
  TimeResolutionEnum,
} from 'common/proxies/enum-proxy';
import {cycle} from 'common/utils/array-util';
import {getDuration} from 'common/utils/date-util';
import {
  getColorsForResource,
  spectrum,
  spectrumHover,
} from 'common/utils/resource-color-util';
import {capitalize, truncate} from 'common/utils/string-util';
import {t} from 'common/utils/translation-util';
import {formatReadout, formatUnits} from 'common/utils/units-util';
import {isNumberValid} from 'common/validators/number-validator';
import Colors from 'common/constants/colors';

// Returns unit/currency label from report meta
export const getUnitString = (meta, short = false) => {
  const {currency, unit} = meta;

  if (currency) {
    return currency.symbol || currency.id;
  }

  if (short) {
    return (unit && unit.shortName) || '';
  }
  return (unit && unit.displayName) || '';
};
export const getUnitStringShort = (meta) => getUnitString(meta, true);

// Returns a flag indicating whether background
// series should be present or not.
export const getBackgroundSeriesFlag = (data, comparisonType) => {
  return Boolean(data && data.length && comparisonType !== LINE_SERIES);
};

// Returns a height, in pixels, for horizontal bars.
// If bar values are shown as text, bars need to be
// taller to accommodate the text elements.
export const getBarHeight = (showValues) => {
  return showValues ? 60 : 30;
};

// Returns a column spacing amount, in pixels.
// Charts default to 2px of spacing between columns,
// but there are cases - mostly based on the length
// of the time period - where you might want to be
// able to render a different amount. The point
// trends card makes use of this functionality.
export const getColumnSpacingByPeriod = (period) => {
  if (!period) {
    return 2;
  }
  switch (period.slug) {
    case ReportingPeriodEnum.LAST_MONTH.slug:
    case ReportingPeriodEnum.MONTH.slug:
      return 3;
    default:
      return 10;
  }
};

// Returns a column spacing amount, in pixels.
// Charts default to 2px of spacing between columns.
// But if there are a large number of readings,
// the spacing needs to be reduced to have space to
// render the data.
export const getColumnSpacingByData = (data) => {
  return data && data.length > 85 ? 0 : 2;
};

// Returns a flag indicating whether or not a comparison series
// should clamp to zero.
// If true, the chart should render any affected series starting
// at zero. If false, the chart should use the lowest value
// available. This is useful for data like temperature, which
// makes more sense rendered over its actual range, rather than
// pegged arbitrarily to zero.
export const getComparisonClamp = (comparisonType) => {
  if (comparisonType) {
    switch (comparisonType.id) {
      case ReportComparisonEnum.CDD.id:
      case ReportComparisonEnum.HDD.id:
      case ReportComparisonEnum.OUTDOOR_TEMPERATURE.id:
        return false;
      default:
        return true;
    }
  }
  return true;
};

// Returns a capitalized title for the comparison series,
// for use in the legend.
export const getComparisonDisplayName = (comparison) => {
  if (comparison && comparison.displayName) {
    return capitalize(comparison.displayName);
  }
  return '';
};

/* Returns a comparison display name, if one exists. */
export const getComparisonName = (meta) => {
  if (meta && meta.comparison && meta.comparison.displayName) {
    return meta.comparison.displayName;
  }
  return '';
};

// Returns specific color for given comparison if one is defined.
// Otherwise, returns undefined.
export const getComparisonSeriesColor = (
  comparison,
  mainColors,
  spectrumColors,
) => {
  if (!comparison) {
    return undefined;
  }
  if (comparison.isPoint && comparison.resource) {
    const comparisonColors = getColorsForResource(comparison.resource.slug);
    const pointColors = getPointColors(
      [mainColors, comparisonColors],
      spectrumColors,
    );
    return pointColors[1].fill;
  }
  switch (comparison.id) {
    case ReportComparisonEnum.CDD.id:
      return Colors.blue400;
    case ReportComparisonEnum.HDD.id:
      return Colors.red400;
    default:
      return undefined;
  }
};

// Returns a style object for a comparison series.
// If the series is a line, we need stroke styles
// for a line. Otherwise we need fill styles for columns.
export const getComparisonSeriesStyle = (
  comparisonSeriesType,
  comparisonSeriesColor,
) => {
  switch (comparisonSeriesType) {
    case LINE_SERIES:
      return {
        stroke: comparisonSeriesColor,
      };
    default:
      return {
        fill: comparisonSeriesColor,
      };
  }
};

/* Returns a comparison series type. */
export const getComparisonSeriesType = (comparison, chartType) => {
  // These comparisons should always render as a line.
  const lineSeriesComparisons = [
    ReportComparisonEnum.PRESSURE.id,
    ReportComparisonEnum.CDD.id,
    ReportComparisonEnum.HDD.id,
    ReportComparisonEnum.IRRADIANCE.id,
    ReportComparisonEnum.HUMIDITY.id,
    ReportComparisonEnum.OUTDOOR_TEMPERATURE.id,
    ReportComparisonEnum.PRECIPITATION.id,
    ReportComparisonEnum.WIND_SPEED.id,
    ReportComparisonEnum.PM25.id,
    ReportComparisonEnum.PM10.id,
    ReportComparisonEnum.NO2.id,
    ReportComparisonEnum.CO.id,
    ReportComparisonEnum.SO2.id,
    ReportComparisonEnum.O3.id,
  ];
  if (comparison && lineSeriesComparisons.indexOf(comparison.id) >= 0) {
    return LINE_SERIES;
  }

  // Trended point comparisons always render as a line.
  if (comparison.isPoint) {
    return LINE_SERIES;
  }

  if (chartType) {
    switch (chartType.id) {
      case ReportChartTypeEnum.LINE.id:
        return LINE_SERIES;
      case ReportChartTypeEnum.DOT.id:
        return DOT_SERIES;
      default:
        return COLUMN_STEP_SERIES;
    }
  }
};

/* Returns a slightly darkened version of the passed color. */
export const getDarkened = (color) => {
  // We need some prophylaxis, because chroma
  // will throw an exception on encountering
  // a string that doesn't contain a valid
  // color in one of the accepted formats.
  return color && typeof color === 'string'
    ? chroma(color).darken(0.2).saturate(1).hex()
    : '';
};

// Returns an array of data for a difference series.
// Takes main data and comparison data and returns a
// new data series of the difference between them.
export const getDataForDifferenceSeries = (
  mainData,
  comparisonData,
  displayName,
  differenceColor,
) =>
  mainData.map((datum, index) => {
    if (comparisonData && comparisonData[index]) {
      const comparisonValue = comparisonData[index].value;
      return {
        color: getDifferenceColor(
          datum.value,
          comparisonValue,
          differenceColor,
        ),
        date: datum.date,
        expected: comparisonValue,
        label: displayName ? truncate(displayName) : null,
        readout: getDifferenceReadout(datum.value, comparisonValue),
        value: datum.value,
      };
    }
    return {};
  });

/* Returns an array of data for a trends series. */
export const getDataForTrendsMainSeries = (
  data,
  unitString,
  resolution,
  normalizerUnit,
  period,
  legendLabel,
  tickFormat,
) => {
  const dateFormattingTokens = getDateFormatForResolution(resolution);
  const shouldKeepTime = !(
    resolution && resolution.slug === TimeResolutionEnum.DAY.slug
  );
  return data.map((datum) => ({
    ...datum,
    ...formatReadout(unitString, datum.value, normalizerUnit),
    dateFormatted: formatDate(datum.date, dateFormattingTokens, shouldKeepTime),
    label: truncate(legendLabel) || null,
    tickFormat,
  }));
};

/* Returns an array of data for a YOY trends series. */
export const getDataForYearOverYearTrendsSeries = (
  data,
  unit,
  dateFormattingTokens,
  normalizerUnit,
) =>
  data.map((datum) => ({
    ...datum,
    ...formatReadout(unit, datum.value, normalizerUnit),

    // The formatting operation will result in a month and a year,
    // eg January 2017. YOY trends is an unusual case in which we
    // just want the month, without pinning it to a specific year,
    // so we split off the year.
    dateFormatted: formatDate(datum.date, dateFormattingTokens)
      .split(' ')
      .shift(),

    // For YOY trends, putting the series display name in
    // tooltip items is inappropriate. We need to show the
    // relevant year for each item instead.
    label: formatDate(datum.date, DateTimeFormat.YEAR),
    tickFormat: DateTimeFormat.MONTH,
  }));

// Returns a string of Moment date formatting
// tokens for a given time resolution.
export const getDateFormatForResolution = (
  resolution = {},
  useMonthOnly = false,
) => {
  switch (resolution.slug) {
    case TimeResolutionEnum.WEEK.slug:
      return DateTimeFormat.TEXTUAL;
    case TimeResolutionEnum.MONTH.slug:
      if (useMonthOnly) {
        return DateTimeFormat.MONTH;
      }
      return DateTimeFormat.MONTH_YEAR;
    case TimeResolutionEnum.YEAR.slug:
      return DateTimeFormat.YEAR;
    default:
      return DateTimeFormat.TEXTUAL_DAY_OF_WEEK;
  }
};

/* Returns the percentage difference between two values. */
export const getDifference = (mainValue, comparisonValue) => {
  let percent = mainValue / comparisonValue;
  percent = isNumberValid(percent) ? percent : 1;

  if (percent < 1 && percent !== 0) {
    return (1 - percent) * 100;
  }
  if (percent > 1) {
    return (percent - 1) * 100;
  }
  return 0;
};

/* Returns a color representing a difference, either green or red. */
export const getDifferenceColor = (
  mainValue,
  comparisonValue,
  differenceColor,
) => {
  let colors = [Colors.gray400];

  if (isNumberValid(mainValue) && isNumberValid(comparisonValue)) {
    if (mainValue > comparisonValue) {
      colors = [Colors.red300, Colors.green300];
    }
    if (mainValue < comparisonValue) {
      colors = [Colors.green300, Colors.red300];
    }
  }

  // user can specify difference colors on configuration
  const greenIncreaseConfigured =
    differenceColor &&
    differenceColor.id === ReportDifferenceColorEnum.GREEN_INC__RED_DEC.id;

  if (greenIncreaseConfigured) {
    colors.reverse();
  }

  return colors.shift();
};

/* Returns a formatted difference value. */
export const getDifferenceFormatted = (difference) => {
  if (isNumberValid(difference)) {
    return formatNumber(difference);
  }
  return '';
};

/* Returns the percentage difference between two values. */
export const getDifferencePercent = (mainValue, comparisonValue) => {
  if (isNumberValid(mainValue) && isNumberValid(comparisonValue)) {
    return mainValue / comparisonValue;
  }
  return 0;
};

/* Returns a formatted readout for a single difference value. */
export const getDifferenceReadout = (mainValue, comparisonValue) => {
  if (isNumberValid(mainValue) && isNumberValid(comparisonValue)) {
    const differenceSymbol = getDifferenceSymbol(mainValue, comparisonValue);
    const difference = getDifference(mainValue, comparisonValue);
    if (difference === 0) return '--';
    const differenceFormatted = getDifferenceFormatted(difference);
    const readout = differenceSymbol
      .concat(differenceFormatted)
      .concat(differenceFormatted === '' ? ' ' : '%');
    return readout;
  }
  return '';
};

// Returns a flag indicating whether or not a difference series
// should be present for a timeseries chart.
// Note - this logic is mirrored in report
// forms' choices_difference_color_WIDGET
export const getDifferenceSeriesFlag = (
  comparisonData,
  mainSeriesType,
  comparison,
) => {
  if (comparisonData && comparisonData.length && comparison) {
    const isDifferenceComparison = [
      ReportComparisonEnum.PREVIOUS_PERIOD.id,
      ReportComparisonEnum.SAME_PERIOD_PREVIOUS_YEAR.id,
      ReportComparisonEnum.GOAL_LINE.id,
      ReportComparisonEnum.PROJECT_BASELINE.id,
    ].includes(comparison.id);
    const isBaseline =
      typeof comparison.id === 'string' &&
      comparison.id.startsWith('baseline_');
    return isDifferenceComparison || isBaseline;
  }
  return false;
};

// Returns a plus or minus symbol for use in a difference readout, depending on
// whether the main value is greater or less than the comparison value.
export const getDifferenceSymbol = (mainValue, comparisonValue) => {
  if (isNumberValid(mainValue) && isNumberValid(comparisonValue)) {
    if (mainValue > comparisonValue) {
      return '+';
    }
    if (mainValue < comparisonValue) {
      return '-';
    }
  }
  return '';
};

// Returns an empty string if the passed display name is for "dimensionless".
// This unit type requires a special exception. See BOS-21934.
export const getGroupLabel = (displayName) => {
  if (displayName && displayName.toLowerCase().includes('dimensionless')) {
    return '';
  }
  return displayName;
};

/* Returns an array of groups for use with Comparison bar charts. */
export const getGroupsForComparisons = (report) => {
  const {
    comparison,
    comparisonUnits,
    currency,
    differenceColor,
    normalizerUnit,
    resource,
    show_values,
    unit2,
  } = report.meta;
  const unit = report.meta.unit || {};

  const colors = getColorsForResource(resource && resource.slug);
  const comparisonUnit = getSecondaryUnit(unit, unit2, comparisonUnits);

  let singleUnit = true;
  if (comparisonUnit.id) {
    if (unit.id && unit.id !== comparisonUnit.id) {
      singleUnit = false;
    } else if (currency && currency.id !== comparisonUnit.id) {
      singleUnit = false;
    }
  }

  const currencyComparison = currency && comparison && singleUnit;
  const comparisonUnitString = getUnitString(
    {
      unit: currencyComparison ? null : comparisonUnit,
      currency: currencyComparison ? currency : null,
    },
    true,
  );

  const max = getMax(report.data, report.comparison);
  const style = {
    fill: colors.underText,
    fillActive: colors.underTextHover,
  };

  const unitString = getUnitStringShort(report.meta);

  // Main data, from which other data shall be derived
  const mainData = report.data.map((datum) => {
    const {readout} = formatReadout(
      unitString,
      datum.value,
      normalizerUnit,
      formatNumber,
    );
    return {
      detailLabel: show_values ? readout : null,
      label: datum.name,
      readout,
      style,
      value: datum.value,
    };
  });

  // Background bar series
  const backgroundBarSeries = getSeriesForGroup({
    data: mainData.map(() => ({
      value: max,
    })),
    excludeFromLegend: true,
    excludeFromTooltip: true,
    interactive: false,
    style: {
      fill: Colors.gray200,
    },
    type: BAR_SERIES,
  });

  // Main bar series
  const barSeries = getSeriesForGroup({
    data: mainData.map((datum) => ({
      readout: datum.readout,
      style: datum.style,
      value: datum.value,
    })),
    legendLabel: capitalize(resource ? resource.displayName : report.name),
    style,
    type: BAR_SERIES,
  });

  // Bar label series
  // Why are the labels in a separate series?
  // We need them to appear on top of everything else.
  // If we render them as part of the main bar series,
  // they get obscured by other elements.
  const barLabelSeries = getSeriesForGroup({
    data: mainData.map((datum) => ({
      detailLabel: datum.detailLabel,
      label: datum.label,
    })),
    excludeFromLegend: true,
    excludeFromTooltip: true,
    type: BAR_SERIES,
  });

  // Overlay bar step series
  let barStepSeries = null;
  if (report.comparison) {
    const comparisonDisplayName = getComparisonDisplayName(comparison);
    barStepSeries = getSeriesForGroup({
      data: report.comparison.map((datum) => ({
        ...datum,
        ...formatReadout(
          comparisonUnitString,
          datum.value,
          normalizerUnit,
          formatNumber,
        ),
        style: {
          fill: Colors.gray400,
        },
      })),
      legendLabel: capitalize(comparisonDisplayName),
      style: {
        fill: Colors.gray400,
      },
      type: BAR_STEP_SERIES,
    });
  }

  // Difference bar series
  let barDifferenceSeries = null;
  if (report.comparison && singleUnit) {
    barDifferenceSeries = getSeriesForGroup({
      data: getDataForDifferenceSeries(
        mainData,
        report.comparison,
        null,
        differenceColor,
      ),
      excludeFromLegend: true,
      legendIndex: Infinity,
      legendLabel: t('Difference'),
      mask: STRIPE,
      style: {
        fill: Colors.gray400,
      },
      type: DIFFERENCE_BAR_SERIES,
    });
  }

  // Groups
  if (singleUnit) {
    // If units match, all series go in same group.
    const series = getSeriesConcatenated(
      backgroundBarSeries,
      barSeries,
      barDifferenceSeries,
      barStepSeries,
      barLabelSeries,
    );
    return [
      {
        showAxis: true,
        clampToZero: true,
        series,
      },
    ];
  }
  // If units do not match, overlay goes in its own group.
  const series = getSeriesConcatenated(
    backgroundBarSeries,
    barSeries,
    barDifferenceSeries,
    barLabelSeries,
  );
  return [
    {
      clampToZero: true,
      series,
      showAxis: true,
    },
    {
      clampToZero: getComparisonClamp(comparison),
      series: [barStepSeries],
      showAxis: false,
    },
  ];
};

/* Returns an array of groups for use with a competition rankings bar chart. */
export const getGroupsForCompetitionRankings = (report) => {
  const {competition, metric, show_values, units} = report.meta;

  // FIXME isPending and isPercent should compare against enums
  const isPending = competition.status === 'pending';
  const isPercent = metric === 'percentReduction';
  const max = getMax(report.data);

  // Background bar series
  const backgroundSeries = getSeriesForGroup({
    data: report.data.map((datum) => ({
      date: datum.date,
      value: max,
    })),
    excludeFromLegend: true,
    excludeFromTooltip: true,
    interactive: false,
    style: {
      fill: Colors.gray200,
    },
    type: BAR_SERIES,
  });

  // Main bar series
  const mainSeries = getSeriesForGroup({
    data: report.data
      .map((datum) => {
        const sign = Math.sign(datum.value);
        const change = {
          '-1': t(' increased'),
          0: '',
          1: t(' reduced'),
        }[sign];
        const detailIcon =
          show_values &&
          {
            '-1': '/static/www/icons/up.svg',
            0: '',
            1: '/static/www/icons/down.svg',
          }[sign];
        const labelNumber = isPercent
          ? formatNumber(Math.abs(datum.value), false, '0.0')
          : formatNumber(Math.abs(datum.value));
        const unitDisplay = isPercent
          ? '%'
          : ` ${formatUnits(units.shortName)}`;
        const detailLabel =
          show_values && !isPending
            ? `${labelNumber}${unitDisplay}${change}`
            : '';
        return {
          ...datum,
          detailLabel,
          detailIcon,
          label: datum.name,
          value: datum.value,
        };
      })
      // It should not be necessary to sort competition rankings,
      // but there are conditions under which rankings do arrive
      // out of order (see BOS-19976). As a consequence, we
      // perform a prophylactic sort here just to make sure
      // that results line up visually.
      // This sort is also performed in the getTrophyData() method
      // of the CompetitionRankingsCard component.
      .sort((datum1, datum2) => {
        return isPercent
          ? datum2.percentReduction - datum1.percentReduction
          : datum2.totalReduction - datum1.totalReduction;
      }),
    legendLabel: competition.resourceType.displayName,
    style: {
      fill: Colors.green100,
      fillActive: Colors.green200,
    },
    type: BAR_SERIES,
  });

  // Groups
  return [
    {
      clampToZero: true,
      series: [backgroundSeries, mainSeries],
      showAxis: true,
    },
  ];
};

/* Returns an array of groups for use with a heatmap. */
export const getGroupsForHeatmap = (report) => {
  const unitString = getUnitStringShort(report.meta);
  return [
    {
      // clampToZero is ignored in chart if
      // passing customColorRanges
      clampToZero: true,
      series: [
        {
          data: report.data,
          legendLabel: unitString,
        },
      ],
    },
  ];
};

/* Returns an array of groups for use with a stacked breakdown timeseries chart. */
export const getGroupsForStackedBreakdown = (report) => {
  const {end, normalizerUnit, resolution, start} = report.meta;

  // Comparison series longer than spectrum require a
  // lengthened spectrum, which repeats itself 3x.
  const repeatedSpectrum = [...spectrum, ...spectrum, ...spectrum];
  const repeatedSpectrumHover = [
    ...spectrumHover,
    ...spectrumHover,
    ...spectrumHover,
  ];
  const dateFormattingTokens = getDateFormatForResolution(resolution);
  const shouldKeepTime = !(
    resolution && resolution.slug === TimeResolutionEnum.DAY.slug
  );
  const unitString = getUnitString(report.meta);
  const unitStringShort = getUnitStringShort(report.meta);

  const duration = getDuration(start, end);
  const tickFormat = getTimeTickFormat(resolution, duration);

  return [
    {
      dividers: true,
      label: truncate(getGroupLabel(unitString)),
      series: report.comparison.map((item, index) => ({
        data: item.data.map((datum) => ({
          ...datum,
          ...formatReadout(unitStringShort, datum.value, normalizerUnit),
          dateFormatted: formatDate(
            datum.date,
            dateFormattingTokens,
            shouldKeepTime,
          ),
          tickFormat,
        })),
        legendLabel: item.meta.legendLabel,
        style: {
          fill: repeatedSpectrum[index],
          fillActive: repeatedSpectrumHover[index],
        },
      })),
      type: STACKED_COLUMN_SERIES,
    },
  ];
};

/* Returns an array of groups for a trends timeseries chart. */
export const getGroupsForTrends = (report) => {
  const {
    chartType,
    comparison,
    comparisonNormalizerUnit,
    comparisonUnits,
    currency,
    differenceColor,
    end,
    normalizerUnit,
    period,
    resolution,
    resource,
    resource2,
    start,
    unit2,
  } = report.meta;

  const unit = report.meta.unit || {};

  const colors = getColorsForResource(resource && resource.slug);
  const comparisons = report.comparison || [];
  const hasPointComparisons =
    comparisons && comparisons.some((comp) => comp.meta && comp.meta.isPoint);
  const useAlternateColors =
    hasPointComparisons && chartType.id === ReportChartTypeEnum.LINE.id;
  const showBackgroundColor = report.meta.showBackgroundColor !== false;
  const style = {
    fill: colors.primary,
    fillActive: useAlternateColors
      ? colors.underTextHover
      : colors.primaryHover,
  };

  const duration = getDuration(start, end);
  const tickFormat = getTimeTickFormat(resolution, duration);

  // Main series
  const mainSeriesLegendLabel =
    (hasPointComparisons && report.meta?.point?.displayName) ||
    getLegendLabelForTrends(resource, report.name, hasPointComparisons);
  const unitString = getUnitString(report.meta, true);

  const mainData = getDataForTrendsMainSeries(
    report.data,
    unitString,
    resolution,
    normalizerUnit,
    period,
    mainSeriesLegendLabel,
    tickFormat,
  );
  const columnSpacing = getColumnSpacingByData(mainData);
  const mainSeriesType = getMainSeriesType(chartType);
  const mainSeriesStyle = getMainSeriesStyle(
    mainSeriesType,
    style,
    showBackgroundColor,
  );
  const mainSeries = getSeriesForGroup({
    columnSpacing,
    data: mainData,
    legendLabel: mainSeriesLegendLabel,
    style: mainSeriesStyle,
    type: mainSeriesType,
  });

  if (comparison) {
    // single comparison defined in the report.meta for old API schema
    // TODO: remove this code block once we fully switch to new API format for
    //  multiple comparisons (BOS-23060)
    const comparisonUnit = getSecondaryUnit(unit, unit2, comparisonUnits);

    let singleUnit = true;
    if (comparisonUnit.id) {
      if (unit.id && unit.id !== comparisonUnit.id) {
        singleUnit = false;
      } else if (currency && currency.id !== comparisonUnit.id) {
        singleUnit = false;
      }
    }

    const currencyComparison = currency && comparison && singleUnit;
    const comparisonUnitString = getUnitString(
      {
        unit: currencyComparison ? null : comparisonUnit,
        currency: currencyComparison ? currency : null,
      },
      true,
    );

    let comparisonSeries = null;
    let comparisonData = [];
    let comparisonSeriesType = null;
    if (comparison) {
      comparisonSeriesType = getComparisonSeriesType(comparison, chartType);
      const comparisonDisplayName = getComparisonDisplayName(comparison);
      comparisonData = getDataForDifferenceSeries(
        mainData,
        report.comparison,
        comparisonDisplayName,
      );

      const comparisonSeriesColor = getComparisonSeriesColor(
        {...comparison, ...resource2},
        colors,
      );
      const comparisonSeriesStyle = getComparisonSeriesStyle(
        comparisonSeriesType,
        comparisonSeriesColor || Colors.gray400,
      );

      comparisonSeries = getSeriesForGroup({
        columnSpacing,
        data: comparisonData.map((datum) => {
          const readout = formatReadout(
            comparisonUnitString,
            datum.expected,
            comparisonNormalizerUnit,
          );
          return {
            ...readout,
            date: datum.date,
            label: datum.label,
            value: datum.expected,
          };
        }),
        legendLabel: comparisonDisplayName,
        style: comparisonSeriesStyle,
        type: comparisonSeriesType,
      });
    }

    // Difference series (if any)
    let differenceColumnSeries = null;
    const shouldHaveDifferenceSeries = getDifferenceSeriesFlag(
      comparisonData,
      mainSeriesType,
      comparison,
    );
    if (shouldHaveDifferenceSeries) {
      differenceColumnSeries = getSeriesForGroup({
        columnSpacing,
        data: comparisonData.map((datum) => {
          return {
            ...datum,
            label: t('Difference'),
            value: isNumberValid(datum.expected) ? datum.value : undefined,
            color: getDifferenceColor(datum.value, datum.expected),
            columnWidth:
              chartType.id === ReportChartTypeEnum.LINE.id ? 10 : undefined,
          };
        }),
        excludeFromLegend: true,
        legendIndex: Infinity,
        legendLabel: t('Difference'),
        mask: STRIPE,
        style: {
          fill: Colors.gray400,
        },
        type: DIFFERENCE_COLUMN_SERIES,
      });
    }

    // Background column series
    let backgroundColumnSeriesMax = null;
    let backgroundColumnSeriesMin = null;
    const shouldHaveBackgroundSeries = getBackgroundSeriesFlag(
      comparisonData,
      comparisonSeriesType,
    );
    if (shouldHaveBackgroundSeries) {
      const max = getMax(
        mainData,

        // When units diverge, the max value in the background
        // column series should be the max value in the main
        // series. Ignore the comparison series.
        singleUnit ? comparisonData : [],
      );
      const min = getMin(mainData, singleUnit ? comparisonData : []);

      // Background column series
      backgroundColumnSeriesMax = getSeriesForGroup({
        columnSpacing,
        data: mainData.map((datum) => ({
          date: datum.date,
          value: max,
        })),
        excludeFromLegend: true,
        excludeFromTooltip: true,
        style: {
          fill: Colors.gray200,
        },
        type: COLUMN_SERIES,
      });
      backgroundColumnSeriesMin = {
        ...backgroundColumnSeriesMax,
        data: mainData.map((datum) => ({
          date: datum.date,
          value: min,
        })),
      };
    }

    // Groups
    const series = getSeriesConcatenated(
      backgroundColumnSeriesMax,
      backgroundColumnSeriesMin,
      mainSeries,
      differenceColumnSeries,
      comparisonSeries,
    );

    const group1 = {
      dividers: true,
      end,
      label: getGroupLabel(unitString),
      series,
      start,
    };

    // No comparison. Return one group:
    if (!comparisonUnit || !comparisonUnit.id) {
      return [group1];
    }

    // Comparison units match main unit, one group is good:
    if (singleUnit) {
      return [group1];
    }

    // Comparison units do not match main unit, return two groups:
    const comparisonClamp = getComparisonClamp(comparison);
    return [
      group1,
      {
        clampToZero: comparisonClamp,
        dividers: true,
        end,
        label: getGroupLabel(comparisonUnit.displayName),
        series: [series.pop()],
        start,
      },
    ];
  }
  // TODO: Remove single comparison implementation once the API
  //   returns comparisons in the new format specified in BOS-23060.
  const comparisonColors = cycle([
    Colors.gray400,
    Colors.gray500,
    Colors.gray600,
  ]);
  const spectrumColors = cycle(spectrum);

  // construct dictionary that maps units to comparisons of that unit
  const unitsToComparison = {};
  const uniqueComparisonUnits = Array.from(
    new Set(
      comparisons.map((comparisonObj) => getUnitString(comparisonObj.meta)),
    ),
  );
  for (const comparisonUnitString of uniqueComparisonUnits) {
    unitsToComparison[comparisonUnitString] = comparisons.filter(
      (comparisonObj) =>
        getUnitString(comparisonObj.meta) === comparisonUnitString,
    );
  }

  // ensure resource colors are not repeated
  const colorSet = new Set([colors.primary]);

  // comparisons with the same unit as the main series should
  // be in the main series group
  let sameUnitComparisonSeries = [];
  const mainUnit = getUnitString(report.meta);
  if (unitsToComparison[mainUnit]) {
    sameUnitComparisonSeries = unitsToComparison[mainUnit].map(
      (comparisonObj, index) => {
        const {isPoint} = comparisonObj.meta;
        let color = getComparisonSeriesColor(
          comparisonObj.meta,
          colors,
          spectrumColors,
        );
        if (!color || colorSet.has(color)) {
          color = isPoint
            ? getUniqueColor(spectrumColors, colorSet)
            : comparisonColors.next().value;
        }
        colorSet.add(color);

        // the first same unit comparison should match the main chart type,
        // additonal comparisons should render a line
        if (index === 0) {
          return getComparisonSeries(comparisonObj, mainData, chartType, color);
        }
        return getComparisonSeries(
          comparisonObj,
          mainData,
          ReportChartTypeEnum.LINE,
          color,
        );
      },
    );
  }

  // Background column series
  let backgroundColumnSeriesMax = null;
  let backgroundColumnSeriesMin = null;
  const shouldHaveBackgroundSeries = comparisons.some((comparisonObj) =>
    getBackgroundSeriesFlag(
      comparisonObj.data,
      getComparisonSeriesType(comparisonObj.meta, chartType),
    ),
  );
  if (shouldHaveBackgroundSeries) {
    // only comparisons with the same unit as the main series should
    // affect the min/max of the background series
    const sameUnitComparisonData = sameUnitComparisonSeries.map(
      (series) => series.data,
    );
    const max = getMax(mainData, ...sameUnitComparisonData);
    const min = getMin(mainData, ...sameUnitComparisonData);

    // Background column series
    backgroundColumnSeriesMax = getSeriesForGroup({
      columnSpacing,
      data: mainData.map((datum) => ({
        date: datum.date,
        value: max,
      })),
      excludeFromLegend: true,
      excludeFromTooltip: true,
      style: {
        fill: Colors.gray200,
      },
      type: COLUMN_SERIES,
    });
    backgroundColumnSeriesMin = {
      ...backgroundColumnSeriesMax,
      data: mainData.map((datum) => ({
        date: datum.date,
        value: min,
      })),
    };
  }

  // the first comparison series with the same unit as the main series
  // renders a difference series
  const differenceComparisonSeries = sameUnitComparisonSeries.shift();

  let differenceColumnSeries = null;
  let shouldHaveDifferenceSeries = false;
  let differenceComparisonMeta = null;
  if (differenceComparisonSeries) {
    differenceComparisonMeta = unitsToComparison[mainUnit][0].meta;
    shouldHaveDifferenceSeries = getDifferenceSeriesFlag(
      differenceComparisonSeries.data,
      mainSeriesType,
      differenceComparisonMeta,
    );
  }
  if (shouldHaveDifferenceSeries) {
    const differenceData = getDataForDifferenceSeries(
      mainData,
      differenceComparisonSeries.data,
      t('Difference'),
      differenceColor,
    );

    // difference column series
    differenceColumnSeries = getSeriesForGroup({
      columnSpacing,
      data: differenceData.map((datum) => ({
        ...datum,
        value: isNumberValid(datum.expected) ? datum.value : undefined,
      })),
      excludeFromLegend: true,
      legendLabel: t('Difference'),
      mask: STRIPE,
      style: {
        fill: Colors.gray400,
      },
      type: DIFFERENCE_COLUMN_SERIES,
    });
  }
  const mainUnitSeries = getSeriesConcatenated(
    backgroundColumnSeriesMax,
    backgroundColumnSeriesMin,
    mainSeries,
    differenceComparisonSeries,
    differenceColumnSeries,
    ...sameUnitComparisonSeries,
  );

  const mainGroup = {
    dividers: true,
    end,
    label: getGroupLabel(mainUnit),
    series: mainUnitSeries,
    start,
  };
  const groups = [mainGroup];

  // construct a group of series for each unique comparison unit
  let hideAxis = false;
  uniqueComparisonUnits.forEach((comparisonUnitString) => {
    if (comparisonUnitString !== mainUnit) {
      const unitComparisons = unitsToComparison[comparisonUnitString];
      const comparisonClamp = getComparisonClamp(unitComparisons[0].meta);
      groups.push({
        clampToZero: comparisonClamp,
        dividers: true,
        end,
        hideAxis,
        label: getGroupLabel(comparisonUnitString),
        series: unitComparisons.map((comparisonObj) => {
          const {isPoint} = comparisonObj.meta;
          let color = getComparisonSeriesColor(
            comparisonObj.meta,
            colors,
            spectrumColors,
          );
          if (!color || colorSet.has(color)) {
            color = isPoint
              ? getUniqueColor(spectrumColors, colorSet)
              : comparisonColors.next().value;
          }
          colorSet.add(color);
          return getComparisonSeries(comparisonObj, mainData, chartType, color);
        }),
        start,
      });

      // only display the y-axis for the first additonal group
      hideAxis = true;
    }
  });

  return groups;
};

/* Returns an array of groups for use with a YOY trends chart. */
export const getGroupsForYearOverYearTrends = (report) => {
  const {resource, normalizerUnit} = report.meta;

  let colors;
  let hoverColors;
  if (report.comparison.length > 2) {
    colors = cycle(spectrum);
    hoverColors = cycle(spectrumHover);
  } else {
    const {primary, underText} = getColorsForResource(
      resource && resource.slug,
    );
    const _colors = [primary, underText];
    const _hoverColors = [getDarkened(primary), getDarkened(underText)];
    if (report.meta.ordering === ReportOrderEnum.ASC.slug) {
      _colors.reverse();
      _hoverColors.reverse();
    }
    colors = cycle(_colors);
    hoverColors = cycle(_hoverColors);
  }

  const columnSpacing = 10;
  const dateFormattingTokens = getDateFormatForResolution(
    TimeResolutionEnum.MONTH,
    true,
  );
  const unitString = getUnitString(report.meta);
  const unitStringShort = getUnitStringShort(report.meta);
  const tickFormat = DateTimeFormat.MONTH;

  const makeSeries = (reportSeries) => {
    const {legendLabel} = reportSeries.meta;
    const data = getDataForYearOverYearTrendsSeries(
      reportSeries.data,
      unitStringShort,
      dateFormattingTokens,
      normalizerUnit,
    );
    return getSeriesForGroup({
      columnSpacing,
      data,
      legendLabel,
      style: {
        fill: colors.next().value,
        fillActive: hoverColors.next().value,
      },
      type: CLUSTERED_COLUMN_SERIES,
    });
  };

  const comparisonSeries = report.comparison.map(makeSeries);

  // Background column series
  const max = getMax(...comparisonSeries.map((series) => series.data));
  const data1 = comparisonSeries[0].data;
  const backgroundColumnSeries = getSeriesForGroup({
    columnSpacing,
    data: data1.map((datum) => ({
      date: datum.date,
      value: max,
      tickFormat,
    })),
    excludeFromLegend: true,
    excludeFromTooltip: true,
    interactive: true,
    style: {
      fill: Colors.white,
      fillActive: Colors.gray200,
    },
    type: COLUMN_SERIES,
  });

  // Groups
  return [
    {
      series: [backgroundColumnSeries],
    },
    {
      series: comparisonSeries,
    },
  ].map((group) => ({
    ...group,
    dividers: true,
    label: getGroupLabel(unitString),
  }));
};

// Returns a layout mode. Options are column and full width.
// If a chart has only line series, it should use full width,
// while all other chart configurations should use column.
export const getLayoutMode = (groups) => {
  return groups
    .map((group) => group.series)
    .reduce((previous, current) => previous.concat(current))
    .map((series) => series.type)
    .filter(
      (type) =>
        [LINE_SERIES, DIFFERENCE_BAR_SERIES, DIFFERENCE_COLUMN_SERIES].indexOf(
          type,
        ) === -1,
    ).length
    ? COLUMN_LAYOUT
    : FULL_WIDTH_LAYOUT;
};

/* Returns a legend label for the main series. */
export const getLegendLabelForTrends = (resource, name) => {
  return resource && resource.displayName ? resource.displayName : name;
};

// Returns a style object for the main series.
// Columns require different styling than lines.
export const getMainSeriesStyle = (seriesType, style, useFill = true) => {
  return seriesType === LINE_SERIES
    ? {
        fill: useFill ? style.fillActive : null,
        stroke: style.fill,
      }
    : {
        ...style,
      };
};

// Returns a series type constant for the main series.
// Rates should render as lines, otherwise columns.
export const getMainSeriesType = (chartType) => {
  if (chartType) {
    switch (chartType.id) {
      case ReportChartTypeEnum.LINE.id:
        return LINE_SERIES;
      case ReportChartTypeEnum.STACKED_COLUMN.id:
        return STACKED_COLUMN_SERIES;
      case ReportChartTypeEnum.DOT.id:
        return DOT_SERIES;
      case ReportChartTypeEnum.CLUSTERED_COLUMN.id:
        return CLUSTERED_COLUMN_SERIES;
      default:
        return COLUMN_SERIES;
    }
  }
  return COLUMN_SERIES;
};

/* Returns the highest value in any number of data series. */
export const getMax = (...args) => {
  return []
    .concat(...args)
    .filter((datum) => datum)
    .map((datum) => datum.expected || datum.value)
    .filter((datum) => datum)
    .reduce(
      (accumulator, currentValue) => Math.max(accumulator, currentValue),
      [],
    );
};

/* Returns the lowest value in any number of data series. */
export const getMin = (...args) => {
  return []
    .concat(...args)
    .filter((datum) => datum)
    .map((datum) => datum.expected || datum.value)
    .filter((datum) => datum)
    .reduce(
      (accumulator, currentValue) => Math.min(accumulator, currentValue),
      [],
    );
};

// Returns the first color in the spectrum that is not in the
// set of existing colors.
export const getUniqueColor = (spectrumColors, existingColorsSet) => {
  let color = spectrumColors.next().value;
  const seen = new Set();
  while (existingColorsSet.has(color) && !seen.has(color)) {
    seen.add(color);
    color = spectrumColors.next().value;
  }
  return color;
};

export const getPointColors = (colors, spectrumColors) => {
  const pointColors = colors.map((color) => ({
    fill: color.primary,
    fillActive: getDarkened(color.primary),
  }));

  // If the second color is the same as the first,
  // use the next spectrum color, or
  // use "underText", which is a darker shade.
  if (pointColors[0].fill === pointColors[1].fill) {
    const fill = spectrumColors
      ? spectrumColors.next().value
      : colors[1].underText;
    pointColors[1] = {
      fill,
      fillActive: getDarkened(fill),
    };
  }

  return pointColors;
};

/* Decides between comparisonUnits and unit2 as the secondary unit. */
export const getSecondaryUnit = (unit, unit2, comparisonUnits) => {
  if (!unit) {
    return comparisonUnits || {};
  }
  if (comparisonUnits) {
    return unit.id === comparisonUnits.id ? unit2 : comparisonUnits;
  }
  return unit2 || {};
};

// Accepts an arbitrary number of series and returns
// an array of them with any falsy indexes filtered out.
export const getSeriesConcatenated = (...args) => {
  return Array.from(args).filter((series) => series);
};

// Returns a series object with the passed options applied.
// If you don't know what these options do, and you want to
// find out, see the chart docs:
// modules/javascript/www/charts/README.md
export const getSeriesForGroup = (options = {}) => ({
  animate: false,
  columnSpacing: 2,
  data: [],
  excludeFromLegend: false,
  excludeFromTooltip: false,
  interactive: true,
  legendIndex: undefined,
  legendLabel: '',
  mask: '',
  style: {},
  type: undefined,
  ...options,
});

// Returns a series object for the given comparison and main data series.
export const getComparisonSeries = (comparison, mainData, chartType, color) => {
  const seriesType = getComparisonSeriesType(comparison.meta, chartType);
  const seriesColor = color || Colors.gray400;
  const seriesStyle = getComparisonSeriesStyle(seriesType, seriesColor);
  const displayName = getComparisonDisplayName(comparison.meta);
  const unitString = getUnitString(comparison.meta, true);

  const data = getDataForDifferenceSeries(
    mainData,
    comparison.data,
    displayName,
  );
  const columnSpacing = getColumnSpacingByData(data);

  return getSeriesForGroup({
    columnSpacing,
    data: data.map((datum) => {
      const readout = formatReadout(
        unitString,
        datum.expected,
        comparison.meta.normalizerUnit,
      );
      return {
        ...readout,
        date: datum.date,
        label: datum.label,
        value: datum.expected,
      };
    }),
    legendLabel: displayName,
    style: seriesStyle,
    type: seriesType,
  });
};

export const getGroupsForPointTrends = (report, isSingleUnit) => {
  const {comparison} = report;
  const {chartType, end, period, resolution, start} = report.meta;

  const seriesType = getMainSeriesType(chartType);

  const columnSpacing = getColumnSpacingByPeriod(period);
  const duration = getDuration(start, end);
  const tickFormat = getTimeTickFormat(resolution, duration);

  const resources = comparison.map(({resource}) => resource && resource.slug);
  const resourceColors = resources.map((resource) =>
    getColorsForResource(resource),
  );
  const resourceColorsPrimary = resourceColors.map((color) => color.primary);
  const noDupeColors =
    new Set(resourceColorsPrimary).size === resourceColorsPrimary.length;

  let pointColors = [];
  if (resources.length === 2 || noDupeColors) {
    pointColors = getPointColors(resourceColors);
  } else {
    const spectrumColors = cycle(spectrum);
    const spectrumHoverColors = cycle(spectrumHover);
    pointColors = comparison.map(() => ({
      fill: spectrumColors.next().value,
      fillActive: spectrumHoverColors.next().value,
    }));
  }

  const unitStrings = comparison.map((comparisonSeries) =>
    getUnitString(comparisonSeries),
  );

  const seriesObjects = comparison.map((comparisonSeries, index) => {
    const {data, label} = comparisonSeries;
    const unitStringShort = getUnitStringShort(comparisonSeries);
    const comparisonData = getDataForTrendsMainSeries(
      data,
      unitStringShort,
      resolution,
      null,
      null,
      null,
      tickFormat,
    );
    return getSeriesForGroup({
      columnSpacing,
      data: comparisonData,
      legendLabel: `${label} (${formatUnits(unitStringShort)})`,
      style: getMainSeriesStyle(seriesType, pointColors[index], false),
      type: seriesType,
    });
  });

  const groups = [];
  if (isSingleUnit) {
    groups.push({
      label: getGroupLabel(unitStrings[0]),
      series: seriesObjects,
    });
  } else {
    // If units don't match, create a group for each
    // unit, where each group has empty filler series
    // for each non-unit group. Keeping groups separate
    // allows visualization of values that are extremely
    // different from other groups (otherwise they may
    // be too small to see).
    const emptyDataSeries = seriesObjects.map((obj) => {
      return {
        ...obj,
        data: obj.data.map((datum) => ({
          date: datum.date,
          value: null,
          tickFormat,
        })),
        excludeFromLegend: true,
        excludeFromTooltip: true,
      };
    });

    const seriesIndicesByUnitString = {};
    unitStrings.forEach((unitString, index) => {
      if (!seriesIndicesByUnitString.hasOwnProperty(unitString)) {
        seriesIndicesByUnitString[unitString] = new Set();
      }
      seriesIndicesByUnitString[unitString].add(index);
    });

    Object.entries(seriesIndicesByUnitString).forEach(
      ([unitString, seriesIndices]) => {
        const series = seriesObjects.map((obj, index) => {
          if (seriesIndices.has(index)) {
            return obj;
          }
          return emptyDataSeries[index];
        });
        groups.push({
          hideAxis: true,
          label: getGroupLabel(unitString),
          series,
        });
      },
    );

    // set last group to not hide axis
    groups[groups.length - 1].hideAxis = false;
  }

  return groups.map((group) => ({
    ...group,
    dividers: true,
    start,
    end,
  }));
};
